/**
 * TLM Beauty Bag Cart
 * Self-contained custom element — does not reuse or modify the theme's
 * default <cart-items> component, so the original cart sections keep
 * working untouched if they are ever re-enabled.
 *
 * Relies on globals already loaded by the theme layout:
 *   - window.routes        (layout/theme.liquid)
 *   - fetchConfig()         (assets/global.js)
 *   - subscribe / publish / PUB_SUB_EVENTS  (assets/pubsub.js, assets/constants.js)
 */
(() => {
  if (customElements.get('tlmc-cart')) return;

  const CHANGE_DEBOUNCE_MS = 300;

  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  class TlmcCart extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this._debouncedInputChange = debounce(this.onInputChange.bind(this), CHANGE_DEBOUNCE_MS);
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('input', this.onInput.bind(this));
      this.addEventListener('change', this.onDirectChange.bind(this));
      // Prevent Enter-in-quantity-field from submitting the form to
      // /cart (full page reload) — the AJAX 'change' handler above
      // already takes care of committing the value.
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && event.target.closest('.tlmc-stepper__input')) {
          event.preventDefault();
          event.target.blur();
        }
      });
    }

    onClick(event) {
      const stepBtn = event.target.closest('[data-action="increase"], [data-action="decrease"]');
      if (stepBtn) {
        event.preventDefault();
        const wrapper = stepBtn.closest('.tlmc-stepper');
        const input = wrapper.querySelector('.tlmc-stepper__input');
        const line = parseInt(wrapper.dataset.line, 10);
        let value = parseInt(input.value || '0', 10);
        const min = parseInt(input.min || '0', 10);
        const max = input.max ? parseInt(input.max, 10) : Infinity;

        value = stepBtn.dataset.action === 'increase' ? value + 1 : value - 1;
        value = Math.max(min, Math.min(value, max));

        input.value = value;
        this.updateLine(line, value);
        return;
      }

      const removeBtn = event.target.closest('.tlmc-remove');
      if (removeBtn) {
        event.preventDefault();
        const line = parseInt(removeBtn.dataset.line, 10);
        this.updateLine(line, 0);
      }
    }

    // Live-updates while typing, debounced.
    onInput(event) {
      const input = event.target.closest('.tlmc-stepper__input');
      if (!input) return;
      this._debouncedInputChange(input);
    }

    // Immediate update on blur / enter (native change event).
    onDirectChange(event) {
      const input = event.target.closest('.tlmc-stepper__input');
      if (!input) return;
      const line = parseInt(input.dataset.line, 10);
      let value = parseInt(input.value || '0', 10);
      if (Number.isNaN(value) || value < 0) value = 0;
      this.updateLine(line, value);
    }

    onInputChange(input) {
      const line = parseInt(input.dataset.line, 10);
      let value = parseInt(input.value || '0', 10);
      if (Number.isNaN(value) || value < 0) value = 0;
      this.updateLine(line, value);
    }

    getErrorEl(line) {
      return this.querySelector(`#TlmcLineItemError-${this.sectionId}-${line}`);
    }

    clearErrors() {
      this.querySelectorAll('.tlmc-item__error').forEach((el) => {
        el.textContent = '';
      });
      const cartErrors = this.querySelector(`#TlmcCartErrors-${this.sectionId}`);
      if (cartErrors) cartErrors.textContent = '';
    }

    setLoading(isLoading) {
      this.classList.toggle('tlmc-is-loading', isLoading);
    }

    getSectionsToRender() {
      const sections = [{ id: this.sectionId, selector: '.tlmc-cart' }];

      if (document.getElementById('cart-icon-bubble')) {
        sections.push({ id: 'cart-icon-bubble', selector: '.shopify-section' });
      }
      if (document.getElementById('cart-live-region-text')) {
        sections.push({ id: 'cart-live-region-text', selector: '.shopify-section' });
      }
      return sections;
    }

    // Directly patches the header cart icon bubble/count. This is a
    // resilient fallback in addition to the section-rendering pass above —
    // it does not depend on "cart-icon-bubble" being wired up as a
    // renderable section in this theme's header.
    updateHeaderCartCount(itemCount) {
      const cartIcon = document.getElementById('cart-icon-bubble');
      if (!cartIcon) return;

      let bubble = cartIcon.querySelector('.cart-count-bubble');

      if (itemCount > 0) {
        if (!bubble) {
          bubble = document.createElement('div');
          bubble.className = 'cart-count-bubble';
          cartIcon.appendChild(bubble);
        }
        let countSpan = bubble.querySelector('span[aria-hidden]');
        if (itemCount < 100) {
          if (!countSpan) {
            countSpan = document.createElement('span');
            countSpan.setAttribute('aria-hidden', 'true');
            bubble.prepend(countSpan);
          }
          countSpan.textContent = itemCount;
        } else if (countSpan) {
          countSpan.remove();
        }
      } else if (bubble) {
        bubble.remove();
      }
    }

    updateLine(line, quantity) {
      if (!line || line < 1) return;
      this.clearErrors();
      this.setLoading(true);

      const sectionsToRender = this.getSectionsToRender();

      const body = JSON.stringify({
        line,
        quantity,
        sections: sectionsToRender.map((s) => s.id),
        sections_url: window.location.pathname,
      });

      fetch(window.routes.cart_change_url, { ...fetchConfig(), ...{ body } })
        .then((response) => response.text())
        .then((state) => {
          const parsedState = JSON.parse(state);

          if (parsedState.errors) {
            const errorEl = this.getErrorEl(line);
            if (errorEl) errorEl.textContent = parsedState.errors;
            const cartErrors = this.querySelector(`#TlmcCartErrors-${this.sectionId}`);
            if (cartErrors && !errorEl) cartErrors.textContent = parsedState.errors;
            this.setLoading(false);
            return;
          }

          sectionsToRender.forEach((section) => {
            const sourceHtml = parsedState.sections[section.id];
            if (!sourceHtml) return;

            const doc = new DOMParser().parseFromString(sourceHtml, 'text/html');
            const newEl = doc.querySelector(section.selector);
            const targetEl = document
              .getElementById(section.id === this.sectionId ? `TlmcCart-${this.sectionId}` : section.id)
              ?.querySelector(section.selector) || document.getElementById(section.id);

            if (newEl && targetEl) {
              targetEl.innerHTML = newEl.innerHTML;
            }
          });

          this.classList.toggle('tlmc-is-empty', parsedState.item_count === 0);
          this.updateHeaderCartCount(parsedState.item_count);
          this.setLoading(false);

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'tlmc-cart',
              cartData: parsedState,
            });
          }
        })
        .catch((error) => {
          console.error('TLM Cart update error:', error);
          this.setLoading(false);
          const cartErrors = this.querySelector(`#TlmcCartErrors-${this.sectionId}`);
          if (cartErrors) {
            cartErrors.textContent = window.cartStrings && window.cartStrings.error
              ? window.cartStrings.error
              : 'Something went wrong, please try again.';
          }
        });
    }
  }

  customElements.define('tlmc-cart', TlmcCart);
})();
